/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DeviceEq20Filled, DocumentText20Filled } from '@fluentui/react-icons';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { SampleButton } from 'src/languageStudio/appShell/prebuiltDemos/components/SampleButton';
import { CallCenterDemoSample, callCenterDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';

type CallCenterSamplesSectionProps = { selectedSample: CallCenterDemoSample; onSampleClick: (sample: CallCenterDemoSample) => void };

const messages = defineMessages({
    dualChannel: { id: 'languageStudio.callCenterSamplesSection.dualChannel', defaultMessage: 'Dual-channel audio' },
    callTranscript: { id: 'languageStudio.callCenterSamplesSection.callTranscript', defaultMessage: 'Call transcript' },
    tryWithSamples: { id: 'languageStudio.callCenterSamplesSection.tryWithSamples', defaultMessage: 'Try with samples' },
    singleChannel: { id: 'languageStudio.callCenterSamplesSection.singleChannel', defaultMessage: 'Single-channel audio' },
    audioSamplesTitle: { id: 'languageStudio.callCenterSamplesSection.audioSamplesTitle', defaultMessage: 'Call audio recording' },
    transcriptSamplesTitle: { id: 'languageStudio.callCenterSamplesSection.transcriptSamplesTitle', defaultMessage: 'Call transcript' },
    chooseSample: { id: 'languageStudio.callCenterSamplesSection.chooseSample', defaultMessage: 'Choose a sample call to view results.' }
});

const theme = getScopedTheme('CallCenterSamplesSection');
const sampleIconStyles = (backgroundColor: string) =>
    classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
        root: { width: '28px', height: '28px', backgroundColor, borderRadius: '5px' }
    });

export const CallCenterSamplesSection = (props: CallCenterSamplesSectionProps) => {
    const { selectedSample, onSampleClick } = props;
    const { localize } = useLocalization();

    const textSamples = callCenterDemoSamples.filter(s => s.sampleType === 'text');
    const audioSamples = callCenterDemoSamples.filter(s => s.sampleType === 'audio');

    const getSampleIconTitle = (sample: CallCenterDemoSample) => {
        if (sample.sampleType === 'text') {
            return localize(messages.callTranscript);
        }

        if (sample.isSingleConversation) {
            return localize(messages.singleChannel);
        }

        return localize(messages.dualChannel);
    };

    const onRenderSample = (sample: CallCenterDemoSample) => (
        <SampleButton
            width={200}
            height={150}
            key={getId()}
            sampleBody={sample.document}
            onClick={() => onSampleClick(sample)}
            data-automation-id={`sampleInputButton-${sample.title}`}
            checked={`${selectedSample?.title}-${selectedSample?.sampleType}` === `${sample.title}-${sample?.sampleType}`}
            sampleTitle={
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Stack
                        verticalAlign="center"
                        horizontalAlign="center"
                        styles={sampleIconStyles(
                            sample.sampleType === 'audio' ? theme.audioIconBackground : theme.transcriptIconBackground
                        )}>
                        {sample.sampleType === 'audio' ? (
                            <DeviceEq20Filled primaryFill={theme.iconFillColor} />
                        ) : (
                            <DocumentText20Filled primaryFill={theme.iconFillColor} />
                        )}
                    </Stack>
                    <LuisText>{getSampleIconTitle(sample)}</LuisText>
                </Stack>
            }
            onRenderBody={() => (
                <Stack grow verticalFill horizontalAlign="start" tokens={{ childrenGap: 10, padding: 8 }}>
                    <LuisText fontWeight={500}>{sample.title}</LuisText>
                    <LuisText variant="smallPlus">{sample.document}</LuisText>
                </Stack>
            )}
        />
    );

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <LuisText as="h2" variant="xLarge" fontWeight={600}>
                {localize(messages.tryWithSamples)}
            </LuisText>

            <Stack tokens={{ childrenGap: 12 }}>
                <LuisText>{localize(messages.chooseSample)}</LuisText>

                <Stack horizontal tokens={{ childrenGap: 20 }}>
                    {audioSamples.map(onRenderSample)}
                    {textSamples.map(onRenderSample)}
                </Stack>
            </Stack>
        </Stack>
    );
};
